import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  ChangeDetectionStrategy,
} from '@angular/core';
import type { OnInit } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { FeatureGuardFacade, GuardedFeature } from '@core/feature-guard-legacy';
import type { OfferSelectOption } from '@core/offers-ui';
import { DropdownComponent, DropdownWidth } from '@core/user-interface-legacy';
import {
  AddEvaluationDataService,
  AddEvaluationDropdownFormService,
  AddEvaluationDropdownStepService,
} from '@recruitee/evaluations-logic';
import { AddEvaluationDropdownStep } from '@recruitee/evaluations-types';
import type { AddEvaluationSubmit } from '@recruitee/evaluations-types';

import { AddEvaluationSelectionStepComponent } from './add-evaluation-selection-step/add-evaluation-selection-step.component';
import { AddEvaluationStepComponent } from './add-evaluation-step/add-evaluation-step.component';

@Component({
  selector: 'rt-add-evaluation-dropdown',
  templateUrl: './add-evaluation-dropdown.component.html',
  styleUrls: ['./add-evaluation-dropdown.component.less'],
  imports: [
    DropdownComponent,
    AddEvaluationSelectionStepComponent,
    AddEvaluationStepComponent,
    NgTemplateOutlet,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    AddEvaluationDropdownStepService,
    AddEvaluationDataService,
    AddEvaluationDropdownFormService,
  ],
})
export class AddEvaluationDropdownComponent implements OnInit {
  private readonly dataService = inject(AddEvaluationDataService);
  private readonly formService = inject(AddEvaluationDropdownFormService);
  private readonly featureGuardFacade = inject(FeatureGuardFacade);
  private readonly cdRef = inject(ChangeDetectorRef);

  private readonly offerId = toSignal(this.formService.offerId$);

  private readonly skipToQuickWhenLocked = effect(() => {
    if (this.isEvaluationFormsEntitled() || this.skippedToQuick) {
      return;
    }

    this.skippedToQuick = true;
    queueMicrotask(() => this.addQuickEvaluation.emit());
  });

  private readonly currentOffer = computed(() =>
    this.offers().find(offer => offer.id === this.offerId()),
  );
  private readonly firstOffer = computed(() => this.offers()[0]);

  private readonly syncCurrentOffer = effect(() =>
    this.dataService.currentOffer.set(this.currentOffer()),
  );
  private readonly syncFirstOffer = effect(() =>
    this.dataService.firstOffer.set(this.firstOffer()),
  );
  private readonly syncCandidateId = effect(() =>
    this.dataService.candidateId.set(this.candidateId()),
  );
  private readonly preselectTemplateId = effect(() => {
    const preselectedTemplateId = this.dataService.preselectedTemplateId();

    if (preselectedTemplateId !== null && !this.formService.form.controls.templateId.value) {
      this.formService.setTemplateId(preselectedTemplateId);
    }
  });

  protected readonly stepService = inject(AddEvaluationDropdownStepService);

  protected readonly DropdownWidth = DropdownWidth;
  protected readonly Step = AddEvaluationDropdownStep;

  protected readonly canManageInterviewTemplates = this.dataService.canManageInterviewTemplates;
  protected readonly recommendedTemplate = this.dataService.recommendedTemplate;

  protected readonly loading = computed(() => !this.dataService.firstOfferTemplatesLoaded());

  protected readonly isEvaluationFormsEntitled = toSignal(
    this.featureGuardFacade.checkFeatureAvailability(GuardedFeature.EvaluationForms),
    { initialValue: true },
  );

  public readonly offers = input<OfferSelectOption[]>([]);
  public readonly candidateId = input<number>();
  public readonly preselectedOfferId = input<number>();
  public readonly hasBackAction = input<boolean>(false);
  public readonly asDropdownTemplate = input<boolean>(false);

  public close = output<void>();
  public addEvaluation = output<AddEvaluationSubmit>();
  public addQuickEvaluation = output<void>();
  public goBack = output<void>();

  private skippedToQuick = false;

  public ngOnInit(): void {
    const [firstOffer] = this.offers();
    const preselectedOfferId = this.preselectedOfferId();

    if (firstOffer || preselectedOfferId) {
      this.formService.setOfferId(preselectedOfferId || Number(firstOffer.id));
      this.cdRef.detectChanges();
    }
  }

  protected onAddRecommendedEvaluation(): void {
    const recommendedTemplate = this.recommendedTemplate();
    const firstOffer = this.firstOffer();

    if (!recommendedTemplate || !firstOffer) {
      return;
    }

    this.addEvaluation.emit({
      templateId: Number(recommendedTemplate.value),
      offerId: Number(firstOffer.id),
    });
  }

  protected onAddQuickEvaluation(): void {
    this.addQuickEvaluation.emit();
  }
}
